/**
 * Расписание ожидаемых осмотров и журнал пропусков.
 *
 * Правило 9: отсутствие осмотра — тоже запись. Ожидаемый по расписанию и не
 * пришедший осмотр логируется с причиной; пустота в журнале недопустима.
 * Именно полнота журнала, а не сама обязательность, даёт доказательную ценность:
 * объяснённый пропуск безопасен, молчаливый превращается в улику.
 *
 * Ожидаемые слоты НЕ материализуются заранее — они выводятся из расписания.
 * Материализуется только пропуск, когда окно закрылось: так нет ни дрейфа,
 * ни необходимости чинить уже созданные записи при правке расписания.
 */
import { z } from 'zod';
import { objectIdSchema } from './common.js';
export const CADENCES = ['daily', 'per_shift'];
/** Местное время в формате HH:MM — интерпретируется в часовом поясе площадки. */
export const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const createScheduleSchema = z.object({
    unitId: objectIdSchema.nullable().default(null),
    siteId: objectIdSchema.nullable().default(null),
    templateKey: z.string().min(1).max(64),
    cadence: z.enum(CADENCES).default('daily'),
    /** Местное время начала окна: ['06:00'] or ['06:00','18:00'] для двух смен. */
    timesOfDay: z.array(localTimeSchema).min(1).max(4),
    /** 1 — понедельник, 7 — воскресенье. null означает все дни. */
    daysOfWeek: z.array(z.number().int().min(1).max(7)).min(1).max(7).nullable().default(null),
    /** Сколько минут после начала окна осмотр ещё считается своевременным. */
    graceMinutes: z.number().int().min(15).max(720).default(180),
    /**
     * Насколько раньше срока осмотр всё ещё засчитывается.
     * Без этого водитель, вышедший на полчаса раньше, создавал бы фантомный пропуск.
     */
    earlyMinutes: z.number().int().min(0).max(720).default(120),
    active: z.boolean().default(true),
});
/** Попадает ли момент в окно слота. */
export function slotContains(slot, at) {
    const t = at.getTime();
    return t >= slot.windowStartsAt.getTime() && t <= slot.windowEndsAt.getTime();
}
export const MISS_REASONS = [
    'unit_not_dispatched',
    'driver_absent',
    'unit_in_shop',
    'app_unavailable',
    'other',
];
export const MISS_REASON_LABELS = {
    unit_not_dispatched: 'Unit did not run',
    driver_absent: 'Driver was absent',
    unit_in_shop: 'Unit in the shop',
    app_unavailable: 'App was unavailable',
    other: 'Other',
};
export const explainMissSchema = z.object({
    reason: z.enum(MISS_REASONS),
    note: z.string().max(2000).nullable().default(null),
});
/* ------------------------- работа с часовыми поясами ---------------------- */
/**
 * Смещение зоны относительно UTC в конкретный момент, в миллисекундах.
 * Считается через Intl, без внешних зависимостей.
 */
export function zoneOffsetMs(instant, timeZone) {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const parts = {};
    for (const part of dtf.formatToParts(instant)) {
        if (part.type !== 'literal')
            parts[part.type] = Number(part.value);
    }
    const asUtc = Date.UTC(parts['year'] ?? 1970, (parts['month'] ?? 1) - 1, parts['day'] ?? 1, 
    // Полночь Intl отдаёт как 24 — приводим к 0.
    (parts['hour'] ?? 0) % 24, parts['minute'] ?? 0, parts['second'] ?? 0);
    return asUtc - instant.getTime();
}
/**
 * Момент UTC для настенного времени в указанной зоне.
 *
 * Смещение применяется дважды: первое приближение может попасть в другую
 * сторону перехода на летнее время, и без пересчёта два раза в год расписание
 * уезжало бы на час, порождая фантомные пропуски.
 */
export function zonedWallTimeToUtc(wall, timeZone) {
    const naive = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
    const firstPass = new Date(naive - zoneOffsetMs(new Date(naive), timeZone));
    return new Date(naive - zoneOffsetMs(firstPass, timeZone));
}
/** День недели по ISO (1 — понедельник) для настенной даты в зоне. */
export function isoWeekdayInZone(instant, timeZone) {
    const name = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(instant);
    const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return order.indexOf(name) + 1;
}
/** Настенная дата в зоне — для перебора дней расписания. */
export function wallDateInZone(instant, timeZone) {
    const dtf = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const [year, month, day] = dtf.format(instant).split('-').map(Number);
    return { year: year ?? 1970, month: month ?? 1, day: day ?? 1 };
}
/**
 * Выводит ожидаемые слоты в диапазоне [from, to).
 * Границы диапазона — моменты UTC; дни перебираются по настенному календарю зоны.
 */
export function expectedSlots(schedule, from, to) {
    if (to.getTime() <= from.getTime())
        return [];
    const slots = [];
    const dayMs = 86_400_000;
    // С запасом на сутки в каждую сторону: слот может начинаться до `from`,
    // но его окно ещё открыто, и наоборот.
    let cursor = new Date(from.getTime() - dayMs);
    const limit = new Date(to.getTime() + dayMs);
    while (cursor.getTime() <= limit.getTime()) {
        const wall = wallDateInZone(cursor, schedule.timeZone);
        for (const time of schedule.timesOfDay) {
            const [hourStr, minuteStr] = time.split(':');
            const dueAt = zonedWallTimeToUtc({
                year: wall.year,
                month: wall.month,
                day: wall.day,
                hour: Number(hourStr),
                minute: Number(minuteStr),
            }, schedule.timeZone);
            if (dueAt.getTime() < from.getTime() || dueAt.getTime() >= to.getTime())
                continue;
            if (schedule.daysOfWeek !== null) {
                if (!schedule.daysOfWeek.includes(isoWeekdayInZone(dueAt, schedule.timeZone)))
                    continue;
            }
            slots.push({
                key: `${schedule.id}@${schedule.unitId}@${dueAt.toISOString()}`,
                scheduleId: schedule.id,
                unitId: schedule.unitId,
                templateKey: schedule.templateKey,
                dueAt,
                windowStartsAt: new Date(dueAt.getTime() - schedule.earlyMinutes * 60_000),
                windowEndsAt: new Date(dueAt.getTime() + schedule.graceMinutes * 60_000),
            });
        }
        cursor = new Date(cursor.getTime() + dayMs);
    }
    return slots
        .filter((slot, index, all) => all.findIndex((s) => s.key === slot.key) === index)
        .sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
}
//# sourceMappingURL=schedule.js.map